import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  alpha,
  useTheme,
  CircularProgress,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  Plus,
  Search,
  Download,
  Send,
  Copy,
  Trash2,
  MoreVertical,
  Eye,
  FileText,
  X,
  TrendingUp,
  Clock,
  CheckCircle2,
} from "lucide-react";
import {
  fetchQuotes,
  downloadQuotePdf,
  sendQuoteEmail,
  duplicateQuote,
  deleteQuote,
  updateQuoteStatus,
} from "../../../../../redux/actions/facturationAction";
import { backendClient } from "../../../../../api/backend";
import DeleteConfirmDialog from "../../Marketing/components/DeleteConfirmDialog";
import { enqueueSnackbar } from "notistack";

const STATUS_CONFIG = {
  draft: { label: "Brouillon", color: "default" },
  sent: { label: "Envoyé", color: "info" },
  accepted: { label: "Accepté", color: "success" },
  rejected: { label: "Refusé", color: "error" },
  expired: { label: "Expiré", color: "warning" },
};

const PLAN_LABELS = {
  starter: "Essentiel",
  pro: "Pro",
  enterprise: "Entreprise",
};

const QuoteList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { quotes, loading } = useSelector((state) => state.facturation);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(null);
  const [statusLoading, setStatusLoading] = useState(null); // quote id being status-updated
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewQuote, setPreviewQuote] = useState(null);
  const [allQuotes, setAllQuotes] = useState([]);

  useEffect(() => {
    dispatch(fetchQuotes({ search, status: statusFilter || undefined }));
  }, [dispatch, search, statusFilter]);

  // Fetch all quotes (unfiltered) for stats
  useEffect(() => {
    backendClient.getQuotes({}).then((data) => {
      setAllQuotes(Array.isArray(data) ? data : data.items || []);
    }).catch(() => {});
  }, [quotes]);

  const handleMenuOpen = (e, quote) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setSelectedQuote(quote);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedQuote(null);
  };

  const handleAction = async (action) => {
    if (!selectedQuote) return;
    const q = selectedQuote;
    handleMenuClose();

    try {
      switch (action) {
        case "view":
          navigate(`/admin/facturation/devis/${q.id}`);
          break;
        case "preview":
          setPdfLoading(q.id);
          try {
            const blob = await backendClient.downloadQuotePdf(q.id);
            const prefix = q.language === "en" ? "Quote" : "Devis";
            const name = q.client_name ? `${prefix} ${q.client_name}.pdf` : `${prefix}.pdf`;
            const file = new File([blob], name, { type: "application/pdf" });
            const url = window.URL.createObjectURL(file);
            setPreviewUrl(url);
            setPreviewQuote(q);
            setPreviewOpen(true);
          } finally {
            setPdfLoading(null);
          }
          break;
        case "pdf":
          setPdfLoading(q.id);
          try {
            await dispatch(downloadQuotePdf(q.id, { clientName: q.client_name, lang: q.language }));
            enqueueSnackbar("PDF téléchargé", { variant: "success" });
          } finally {
            setPdfLoading(null);
          }
          break;
        case "send":
          await dispatch(sendQuoteEmail(q.id));
          enqueueSnackbar("Devis envoyé par email", { variant: "success" });
          break;
        case "duplicate":
          await dispatch(duplicateQuote(q.id));
          enqueueSnackbar("Devis dupliqué", { variant: "success" });
          break;
        case "delete":
          setQuoteToDelete(q);
          setDeleteDialogOpen(true);
          break;
        default:
          break;
      }
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    setStatusLoading(quoteId);
    try {
      await dispatch(updateQuoteStatus(quoteId, newStatus));
      enqueueSnackbar(`Statut mis à jour: ${STATUS_CONFIG[newStatus]?.label}`, { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.message || "Erreur", { variant: "error" });
    } finally {
      setStatusLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatAmount = (amount, currency = "MAD") => {
    if (!amount) return "—";
    return Number(amount).toLocaleString("fr-FR", { minimumFractionDigits: 2 }) + " " + currency;
  };

  // ── Stats ──
  const stats = useMemo(() => {
    const src = allQuotes.length > 0 ? allQuotes : quotes;
    if (!src || src.length === 0) return null;
    const total = src.length;
    const totalTtc = src.reduce((sum, q) => sum + (Number(q.total_ttc) || 0), 0);
    const accepted = src.filter((q) => q.status === "accepted");
    const acceptedTtc = accepted.reduce((sum, q) => sum + (Number(q.total_ttc) || 0), 0);
    const pending = src.filter((q) => q.status === "draft" || q.status === "sent");
    const pendingTtc = pending.reduce((sum, q) => sum + (Number(q.total_ttc) || 0), 0);
    return { total, totalTtc, acceptedCount: accepted.length, acceptedTtc, pendingCount: pending.length, pendingTtc };
  }, [allQuotes, quotes]);

  const STAT_CARDS = stats
    ? [
        {
          label: "Total devis",
          value: stats.total,
          sub: formatAmount(stats.totalTtc),
          icon: FileText,
          color: theme.palette.primary.main,
        },
        {
          label: "En cours",
          value: stats.pendingCount,
          sub: formatAmount(stats.pendingTtc),
          icon: Clock,
          color: "#f59e0b",
        },
        {
          label: "Acceptés",
          value: stats.acceptedCount,
          sub: formatAmount(stats.acceptedTtc),
          icon: CheckCircle2,
          color: "#10b981",
        },
        {
          label: "Taux conversion",
          value: stats.total > 0 ? `${Math.round((stats.acceptedCount / stats.total) * 100)}%` : "—",
          sub: `${stats.acceptedCount} / ${stats.total} devis`,
          icon: TrendingUp,
          color: "#8b5cf6",
        },
      ]
    : [];

  return (
    <Box>
      {/* Stats Cards */}
      {stats && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, mb: 3 }}>
          {STAT_CARDS.map((card) => (
            <Paper
              key={card.label}
              elevation={0}
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "12px",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  bgcolor: alpha(card.color, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <card.icon size={18} color={card.color} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem", fontWeight: 500 }}>
                  {card.label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", lineHeight: 1.2 }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }}>
                  {card.sub}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Toolbar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flex: 1 }}>
          <TextField
            size="small"
            placeholder="Rechercher par client ou numéro..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              sx={{ fontSize: "0.85rem" }}
            >
              <MenuItem value="">Tous les statuts</MenuItem>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <MenuItem key={key} value={key}>
                  {cfg.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => navigate("/admin/facturation/devis/new")}
          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
        >
          Nouveau devis
        </Button>
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>
                  N°
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>
                  Client
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>
                  Plan
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>
                  Utilisateurs
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }} align="right">
                  Total TTC
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>
                  Statut
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                      <FileText size={40} strokeWidth={1} color={theme.palette.text.disabled} />
                      <Typography variant="body2" color="text.secondary">
                        Aucun devis trouvé
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Plus size={14} />}
                        onClick={() => navigate("/admin/facturation/devis/new")}
                        sx={{ mt: 1, textTransform: "none" }}
                      >
                        Créer un devis
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                quotes.map((quote) => (
                  <TableRow
                    key={quote.id}
                    hover
                    sx={{ cursor: "pointer", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                    onClick={() => navigate(`/admin/facturation/devis/${quote.id}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "monospace", fontSize: "0.8rem" }}>
                        {quote.quote_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {quote.client_name}
                      </Typography>
                      {quote.client_contact_name && (
                        <Typography variant="caption" color="text.secondary">
                          {quote.client_contact_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={PLAN_LABELS[quote.plan] || quote.plan}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          height: 22,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{quote.num_users}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatAmount(quote.total_ttc, quote.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {statusLoading === quote.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <FormControl size="small">
                          <Select
                            value={quote.status}
                            onChange={(e) => handleStatusChange(quote.id, e.target.value)}
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              height: 28,
                              "& .MuiSelect-select": { py: 0.5, px: 1 },
                            }}
                            renderValue={(val) => (
                              <Chip
                                label={STATUS_CONFIG[val]?.label || val}
                                color={STATUS_CONFIG[val]?.color || "default"}
                                size="small"
                                sx={{ fontSize: "0.7rem", height: 22, fontWeight: 600 }}
                              />
                            )}
                          >
                            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                              <MenuItem key={key} value={key} sx={{ fontSize: "0.8rem" }}>
                                {cfg.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(quote.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        <Tooltip title="Télécharger PDF">
                          <IconButton
                            size="small"
                            disabled={pdfLoading === quote.id}
                            onClick={() => { setSelectedQuote(quote); handleAction("pdf"); }}
                          >
                            {pdfLoading === quote.id ? <CircularProgress size={15} /> : <Download size={15} />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Plus d'actions">
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, quote)}>
                            <MoreVertical size={15} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Actions menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleAction("view")}>
          <Eye size={14} style={{ marginRight: 8 }} /> Voir / Modifier
        </MenuItem>
        <MenuItem onClick={() => handleAction("preview")}>
          <Eye size={14} style={{ marginRight: 8 }} /> Aperçu PDF
        </MenuItem>
        <MenuItem onClick={() => handleAction("pdf")}>
          <Download size={14} style={{ marginRight: 8 }} /> Télécharger PDF
        </MenuItem>
        <MenuItem onClick={() => handleAction("send")} disabled={!selectedQuote?.client_contact_email}>
          <Send size={14} style={{ marginRight: 8 }} /> Envoyer par email
        </MenuItem>
        <MenuItem onClick={() => handleAction("duplicate")}>
          <Copy size={14} style={{ marginRight: 8 }} /> Dupliquer
        </MenuItem>
        {selectedQuote?.status === "draft" && (
          <MenuItem onClick={() => handleAction("delete")} sx={{ color: "error.main" }}>
            <Trash2 size={14} style={{ marginRight: 8 }} /> Supprimer
          </MenuItem>
        )}
      </Menu>

      {/* PDF Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
          setPreviewQuote(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "90vh", borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Aperçu — {previewQuote?.quote_number || "Devis"}
          </Typography>
          <IconButton size="small" onClick={() => {
            setPreviewOpen(false);
            if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
            setPreviewQuote(null);
          }}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewUrl && (
            <iframe
              src={previewUrl}
              title="Aperçu du devis"
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setQuoteToDelete(null); }}
        onConfirm={async () => {
          if (!quoteToDelete) return;
          await dispatch(deleteQuote(quoteToDelete.id));
          enqueueSnackbar("Devis supprimé", { variant: "success" });
          setDeleteDialogOpen(false);
          setQuoteToDelete(null);
        }}
        itemName={quoteToDelete?.quote_number || ""}
        itemType="devis"
      />
    </Box>
  );
};

export default QuoteList;
